import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ArrowRight, MessageSquare, BarChart3, BrainCircuit } from "lucide-react";
import HeroParticles from "@/components/HeroParticles";

const fadeInUp = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
};

export default function HeroSection() {
  const scrollTo = (id) => {
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen hero-gradient-bg flex items-center lg:pt-20"
    >
      {/* Animated gradient background */}
      <div className="absolute inset-0 hero-animated-gradient" />
      {/* Dandelion / radial burst canvas */}
      <HeroParticles />
      {/* Subtle glow orbs */}
      <div className="absolute top-1/4 left-[15%] w-[500px] h-[500px] rounded-full bg-stripe-primary/[0.07] blur-[100px]" />
      <div className="absolute bottom-1/3 right-[10%] w-[400px] h-[400px] rounded-full bg-cyan-400/[0.06] blur-[100px]" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-8 pt-28 pb-12 lg:pt-0 lg:pb-0 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left content */}
          <motion.div {...fadeInUp} className="text-left">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-stripe-primary/[0.08] border border-stripe-primary/10 mb-8">
              <span className="w-2 h-2 rounded-full bg-stripe-primary animate-pulse" />
              <span className="text-xs font-semibold text-stripe-primary tracking-wide uppercase">AI-Powered Soft Skills Training</span>
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-[1.08]" style={{ color: '#0A2540' }}>
              Master Every{" "}
              <span className="bg-gradient-to-r from-stripe-primary to-cyan-400 bg-clip-text text-transparent">
                Conversation
              </span>
              <br />
              Before It Happens
            </h1>
            <p className="text-base md:text-lg leading-relaxed max-w-lg mb-10" style={{ color: '#425466' }}>
              The AI simulator that trains you in negotiation, sales, and difficult
              conversations through realistic dialogue. Practice,<br className="hidden md:inline" /> get scored, and
              improve — risk-free.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                data-testid="hero-cta-primary"
                onClick={() => scrollTo("#demo")}
                className="bg-stripe-primary hover:bg-stripe-primary-hover text-white rounded-full px-8 py-6 text-base font-medium shadow-lg shadow-stripe-primary/20 hover:shadow-xl hover:shadow-stripe-primary/30 transition-all duration-300 group"
              >
                Try the Demo
                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
              </Button>
              <Button
                data-testid="hero-cta-secondary"
                onClick={() => scrollTo("#cta")}
                variant="outline"
                className="rounded-full px-8 py-6 text-base border-stripe-border text-stripe-text-secondary hover:bg-white hover:text-navy hover:border-slate-300 hover:shadow-md transition-all duration-300"
              >
                Request Early Access
              </Button>
            </div>

            {/* Trust bar */}
            <div className="mt-12 flex items-center gap-6 text-sm" style={{ color: '#8898AA' }}>
              <span className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-emerald-500" />
                No signup required
              </span>
              <span className="hidden sm:flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-emerald-500" />
                Free demo
              </span>
            </div>
          </motion.div>

          {/* Right visual - Floating chat cards */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="relative hidden lg:block mt-8"
          >
            <div className="relative">
              {/* Main chat card */}
              <div className="stripe-card rounded-2xl p-6 max-w-md ml-auto animate-float-gentle">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-xl bg-stripe-primary/10 flex items-center justify-center">
                    <MessageSquare className="w-5 h-5 text-stripe-primary" />
                  </div>
                  <div>
                    <div className="text-navy text-sm font-medium">Salary Negotiation</div>
                    <div className="text-stripe-text-muted text-xs">Live simulation</div>
                  </div>
                  <span className="ml-auto px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-600 text-xs font-medium border border-emerald-100">
                    Active
                  </span>
                </div>
                <div className="space-y-3">
                  <div className="chat-bubble-ai rounded-xl rounded-tl-sm px-4 py-3 text-sm max-w-[85%]">
                    I appreciate your work on the Q3 project. What compensation adjustment did you have in mind?
                  </div>
                  <div className="chat-bubble-user rounded-xl rounded-tr-sm px-4 py-3 text-sm ml-auto max-w-[85%]">
                    Based on my impact and market data, I believe a 15% increase reflects my contributions.
                  </div>
                </div>
              </div>

              {/* Floating score card */}
              <div className="stripe-card rounded-xl p-4 absolute -bottom-6 -left-6 w-48" style={{ animationDelay: "2s", animation: "float-gentle 6s ease-in-out infinite 2s" }}>
                <div className="flex items-center gap-2 mb-2">
                  <BarChart3 className="w-4 h-4 text-emerald-500" />
                  <span className="text-navy text-xs font-medium">Performance</span>
                </div>
                <div className="text-2xl font-heading font-bold text-emerald-500">87<span className="text-sm text-stripe-text-muted">/100</span></div>
                <div className="text-xs text-stripe-text-muted mt-1">Excellent confidence level</div>
              </div>

              {/* Floating insight card */}
              <div className="stripe-card rounded-xl p-4 absolute -top-4 -left-12 w-52" style={{ animationDelay: "4s", animation: "float-gentle 6s ease-in-out infinite 4s" }}>
                <div className="flex items-center gap-2 mb-2">
                  <BrainCircuit className="w-4 h-4 text-stripe-primary" />
                  <span className="text-navy text-xs font-medium">AI Coaching</span>
                </div>
                <div className="text-xs text-stripe-text-secondary leading-relaxed">
                  Strong opening. Use specific metrics to strengthen your position.
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
